import React, {useEffect, useContext, lazy, Suspense, forwardRef} from "react";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import {TemplateContext} from "@context/TemplateContext";
import ActivityDashboard from "../nav/ActivityDashboard";
import DynamicContent from "../nav/DynamicContent";
import AdminCMSContent from "../cms/AdminCMSContent";
import SyllabusOverview from "../lesson/SyllabusOverview";
import DiagnosticReports from "../diagnosticReporting/DiagnosticReports";
import CircularLoader from "./CircularLoader";
import AssessmentTabs from "../tests/teacher/Assessments/AssessmentTabs";
import StudentTrails from "../trails/student/StudentTrails";
import StudentTrailLessons from "../trails/student/StudentTrailLessons";
import Diagnostics from "../tests/teacher/Diagnostics/Diagnostics";
import StudentTestAssignments from "../tests/student/assessments/StudentTestAssignments";
import LessonContentStyleGuide from "../lesson/lessonContentStyleGuide/LessonContentStyleGuide";
import StudentDiagnosticReports from "../diagnosticReporting/StudentDiagnosticReports";
import ContentTabs from "../content/ContentTabs";
import Glossary from "../glossary/Glossary";
import Footer from "./Footer";
import Lesson from "../lesson/Lesson";
import LessonPlanManager from "../lessonPlan/LessonPlanManager";
import ManageBookmarks from "../bookmarks/ManageBookmarks";
import {ContentContext} from "@context/ContentContext";
import Messages from "../messages/Messages";
import Competitions from "../competitions/Competitions";
import MyClassesTabs from "../myClasses/MyClassesTabs";
import Games from "../games/Games";
import TeacherTrailTabs from "../trails/teacher/TeacherTrailTabs";
import QuestionFeedback from "../questionFeedback/QuestionFeedback";
import NavigationRightDevelopment from "../nav/NavigationRightDevelopment";
import ClassKey from "../classKey/ClassKey";
import Test from "../tests/student/assessments/Test";
import DiagnosticTest from "../tests/student/assessments/DiagnosticTest";
import OfflineResources from "../resources/OfflineResources";
import {StudentTrail} from "../trails/student/StudentTrail";
import DownloadResources from "../downloadResources/DownloadResources";
import StudentProgressAchievement from "../progress/student/StudentProgressAchievement";
import StudentProgressGeneric from "../progress/student/StudentProgressGeneric";
import FloatingOpenRightNavMobile from "../nav/FloatingOpenRightNavMobile";
import AccountSettings from "../accountSettings/AccountSettings";
import Reports from "../reports/Reports";
import SchoolManagementTabs from "../schoolManagement/SchoolManagementTabs";
import {useScreenMode} from "@context/ScreenModeContext";
import LessonPlanWizardDevelopment from "../lessonPlan/LessonPlanWizardDevelopment";
import LessonPlanManagerDevelopment from "../lessonPlan/LessonPlanManagerDevelopment";
import LessonPlans from "../lessonPlan/LessonPlans";
import LessonPlannerManager from "../lessonPlanner/LessonPlannerManager";
import Announcements from "../messages/Announcements";

const SchoolClasses = lazy(() => import(/* webpackChunkName: "SchoolClasses" */ "../schoolClasses/SchoolClasses"));

const Body = forwardRef((props, ref) => {
    const {smallScreenMode} = useScreenMode();
    const {user} = useContext(UserContext);
    const {content} = useContext(ContentContext);
    const {bodyComponentName, shortcut, shortcutLoading, url, params} = useContext(TemplateContext);
    const brandConfig = useContext(BrandContext);
    const bodyChange = new CustomEvent('ch-bodyChange', {});

    const bodyComponents = {
        ActivityDashboard: ActivityDashboard,
        DynamicContent: DynamicContent,
        Competitions: Competitions,
        LessonContent: Lesson,
        Lesson: Lesson,
        AdminCMSContent: AdminCMSContent,
        LessonContentStyleGuide: LessonContentStyleGuide,
        SyllabusOverview: SyllabusOverview,
        DiagnosticReports: DiagnosticReports,
        StudentDiagnosticReports: StudentDiagnosticReports,
        TeacherDiagnostics: Diagnostics,
        StudentTrails: StudentTrails,
        StudentTrailLessons: StudentTrailLessons,
        TeacherTrailTabs: TeacherTrailTabs,
        TeacherAssessments: AssessmentTabs,
        ContentImport: ContentTabs,
        Games: Games,
        Glossary: (brandConfig.useReactGlossary ? Glossary : DynamicContent),
        StudentTestAssignments: StudentTestAssignments,
        SchoolClasses: SchoolClasses,
        ManageBookmarks: ManageBookmarks,
        Messages: Messages,
        MyClasses: MyClassesTabs,
        QuestionFeedback: QuestionFeedback,
        ClassKey: ClassKey,
        Test: Test,
        DiagnosticTest: DiagnosticTest,
        OfflineResources: OfflineResources,
        StudentTrail: StudentTrail,
        DownloadResources: DownloadResources,
        StudentProgressAchievement: StudentProgressAchievement,
        StudentProgressGeneric: StudentProgressGeneric,
        AccountSettings: AccountSettings,
        Reports: Reports,
        SchoolManagementTabs: SchoolManagementTabs,
        LessonPlanManager: LessonPlanManager,
        LessonPlans: LessonPlans,
        LessonStudio: LessonPlannerManager,
        Announcements: Announcements
    };

    useEffect(() => {
        document.dispatchEvent(bodyChange);
        // console.log('url: ',url);
    },[bodyComponentName, url]);

    useEffect(() => {
        hashOutper.clear();
    }, [bodyComponentName]);

    useEffect(() => {
        !brandConfig.useDevelopmentNavigation && handleTitleScroll();
    }, [bodyComponentName, smallScreenMode]);

    const BodyComponent = bodyComponentName === undefined ? undefined : bodyComponents[bodyComponentName];
    if (BodyComponent === undefined) {
        console.error('BodyComponent is undefined', bodyComponentName);
        return <div className="ch-error-text">Content not available</div>;
    }

    return (
        <main id="contentPanel" ref={ref} className="ch-content-panel" tabIndex={-1}>
            {brandConfig.globalWarningMessage !== undefined && brandConfig.globalWarningMessage.length > 0 && user.authorityId == 1 &&
                <div className={'ch-global-warning-text'} dangerouslySetInnerHTML={{ __html: brandConfig.globalWarningMessage}}/>
            }
            <div className={`ch-content-main cont-body js-body ${shortcut && shortcut.shortcutLocation === 'LESSON' ? 'ch-lesson-body' : 'ch-non-lesson-body'} ${user.cmsMode ? 'ch-cms-wrap' : ''} ${(shortcut && shortcut.shortcutClass ? shortcut.shortcutClass : '')}`}>
                <Suspense fallback={<CircularLoader />}>
                    {React.createElement(BodyComponent, {url: url, entityId: user.cmsMode && content.currentConcept != undefined ? content.currentConcept.conceptId: undefined, params})}
                </Suspense>
                {(brandConfig.useDevelopmentNavigation && !shortcutLoading && user.cmsMode && !smallScreenMode) &&
                    <NavigationRightDevelopment smallScreenMode={smallScreenMode} compressed={false} showResources={shortcut?.shortcutId != 156}/>
                }
                {((brandConfig.useDevelopmentNavigation && !shortcutLoading && user.cmsMode && smallScreenMode)) &&
                    <FloatingOpenRightNavMobile />
                }
            </div>
            <Footer/>
        </main>
    )
})

export default Body;